import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { PaymentReceiptService } from '../../../services/payment-receipt.service';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { CustomerCredit } from '../../../model/common/common.model';

@Component({
  selector: 'app-apply-customer-credits-lookup',
  standalone: true,
  imports: [SharedModule, CommonModule],
  templateUrl: './apply-customer-credits-lookup.component.html',
  styleUrls: ['./apply-customer-credits-lookup.component.scss']
})
export class ApplyCustomerCreditsLookupComponent implements OnInit {

  credits: CustomerCredit[] = [];
  loading = false;
  customerId = 0;
  invoiceNo = '';
  billAmount = 0;
  totalBillAmount = 0;
  remainingBalance = 0;
  appliedCredits = 0;
  appliedTds = 0;
  form: FormGroup;
  displayDialog = true;
  billAmountOrginal = 0;
  priorPaidAmount = 0;

  constructor(
    private fb: FormBuilder,
    private paymentReceiptService: PaymentReceiptService,
    private messageService: MessageService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.form = this.fb.group({
      appliedOnDate: [new Date(), Validators.required]
    });
  }

  ngOnInit(): void {
    this.customerId = this.config.data.customerId;
    this.invoiceNo = this.config.data.invoiceNo || '';
    this.billAmount = parseFloat(this.config.data.billAmount) || 0;
    this.billAmountOrginal = parseFloat(this.config.data.totalBillAmount) || 0;
    this.priorPaidAmount = parseFloat(this.config.data.priorPaidAmount) || 0;
    this.totalBillAmount = parseFloat(this.config.data.totalBillAmount) || this.billAmount;
    this.remainingBalance = this.billAmount;
    this.displayDialog = true;
    this.form.patchValue({ appliedOnDate: new Date() });
    this.loadCustomerCredits();
  }

  getMaxTds(credit: CustomerCredit): number {
    return Math.min(credit.maxTdsApplicable, credit.availableBalance - credit.creditsToApply);
  }

  async loadCustomerCredits(): Promise<void> {
    if (!this.customerId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Customer ID is required', life: 3000 });
      return;
    }

    this.loading = true;
    try {
      const raw: any = await this.paymentReceiptService.getAdvanceCustomerPayments(this.customerId).toPromise();
      const response = Array.isArray(raw) ? raw : (raw?.data ?? raw?.payments ?? []);
      await this.processCreditsResponse(response || []);
      if (this.credits.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'No Credits',
          detail: 'No customer advance credits found. Save an advance-only payment receipt first.',
          life: 4000
        });
      }
    } catch {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load customer credits', life: 3000 });
    } finally {
      this.loading = false;
    }
  }

  private async processCreditsResponse(data: any[]): Promise<void> {
    const credits = data
      .filter(p => {
        const excess = Number(p.excessAmount ?? p.ExcessAmount ?? 0);
        const isAdvance = p.isAdvancePayment ?? p.IsAdvancePayment
          ?? (Number(p.advanceAmount ?? p.AdvanceAmount ?? 0) > 0);
        return excess > 0 && !!isAdvance;
      })
      .map((p) => {
        const totalCredit = Number(p.advanceAmount ?? p.AdvanceAmount ?? p.amountReceived ?? p.AmountReceived ?? 0);
        const availableBalance = Number(p.excessAmount ?? p.ExcessAmount ?? 0);
        return {
          paymentReceiptId: p.paymentReceiptId ?? p.PaymentReceiptId,
          referenceNumber: p.paymentReceiptNumber ?? p.PaymentReceiptNumber ?? p.referenceNumber ?? '',
          date: new Date(p.paymentDate ?? p.PaymentDate),
          description: 'Customer Advance',
          transactionType: p.paymentType ?? p.PaymentType ?? '',
          amount: totalCredit,
          tdsAmount: Number(p.tdsAmount ?? p.TdsAmount ?? 0),
          availableBalance,
          alreadyApplied: totalCredit - availableBalance,
          creditsToApply: 0,
          tdsApplied: 0,
          maxTdsApplicable: Math.min(p.tdsAmount || 0, availableBalance)
        } as CustomerCredit;
      });

    this.credits = credits.filter(c => c.availableBalance > 0);
    this.calculateTotals();
  }

  applyFullCredit(credit: CustomerCredit): void {
    if (this.remainingBalance <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Invoice amount is fully paid', life: 3000 });
      return;
    }

    credit.creditsToApply = 0;
    credit.tdsApplied = 0;
    const currentTotalApplied = this.getTotalApplied();
    const actualRemaining = this.billAmount - currentTotalApplied;
    const maxApplicable = Math.min(credit.availableBalance, actualRemaining);

    if (maxApplicable <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'No remaining balance to apply credits to', life: 3000 });
      this.calculateTotals();
      return;
    }

    credit.creditsToApply = maxApplicable;
    if (credit.maxTdsApplicable > 0) {
      const remainingAfterCredit = actualRemaining - maxApplicable;
      const maxTdsCanApply = Math.min(credit.maxTdsApplicable, remainingAfterCredit, credit.availableBalance - credit.creditsToApply);
      if (maxTdsCanApply > 0) {
        credit.tdsApplied = maxTdsCanApply;
      }
    }

    this.calculateTotals();
  }

  onCreditChange(credit: CustomerCredit): void {
    if (credit.creditsToApply > credit.availableBalance) {
      credit.creditsToApply = credit.availableBalance;
    }
    if (credit.creditsToApply < 0) {
      credit.creditsToApply = 0;
    }
    const maxTdsForThisCredit = Math.min(credit.maxTdsApplicable, credit.availableBalance - credit.creditsToApply);
    if (credit.tdsApplied > maxTdsForThisCredit) {
      credit.tdsApplied = maxTdsForThisCredit;
    }
    this.calculateTotals();
    this.validateTotalApplication();
  }

  onTdsChange(credit: CustomerCredit): void {
    const maxTds = Math.min(credit.maxTdsApplicable, credit.availableBalance - credit.creditsToApply);
    if (credit.tdsApplied > maxTds) {
      credit.tdsApplied = maxTds;
    }
    if (credit.tdsApplied < 0) {
      credit.tdsApplied = 0;
    }
    this.calculateTotals();
    this.validateTotalApplication();
  }

  private getTotalApplied(): number {
    return this.credits.reduce((sum, credit) => sum + (credit.creditsToApply || 0) + (credit.tdsApplied || 0), 0);
  }

  calculateTotals(): void {
    this.appliedCredits = this.credits.reduce((sum, credit) => sum + (credit.creditsToApply || 0), 0);
    this.appliedTds = this.credits.reduce((sum, credit) => sum + (credit.tdsApplied || 0), 0);
    this.remainingBalance = Math.max(0, this.billAmount - this.appliedCredits - this.appliedTds);
  }

  private validateTotalApplication(): void {
    const totalApplied = this.getTotalApplied();
    if (totalApplied > this.billAmount) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: `Total credits applied (₹${totalApplied.toFixed(2)}) exceed current invoice balance (₹${this.billAmount.toFixed(2)})`,
        life: 3000
      });
    }
  }

  save(): void {
    if (!this.form.valid || !this.form.value.appliedOnDate) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select Applied On Date before saving', life: 2000 });
      return;
    }

    if (this.appliedCredits <= 0 && this.appliedTds <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please apply at least one credit', life: 2000 });
      return;
    }

    const totalApplied = this.appliedCredits + this.appliedTds;
    if (totalApplied > this.billAmount) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Cannot apply ₹${totalApplied.toFixed(2)} to an invoice with only ₹${this.billAmount.toFixed(2)} remaining`,
        life: 2000
      });
      return;
    }

    const appliedCredits = this.credits
      .filter(credit => credit.creditsToApply > 0 || credit.tdsApplied > 0)
      .map(credit => ({
        paymentReceiptId: credit.paymentReceiptId,
        referenceNumber: credit.referenceNumber,
        transactionType: credit.transactionType,
        amountApplied: credit.creditsToApply,
        tdsApplied: credit.tdsApplied || 0,
        appliedOn: this.form.value.appliedOnDate
      }));

    this.ref.close({
      invoiceNo: this.invoiceNo,
      customerId: this.customerId,
      appliedCredits,
      totalCreditsApplied: this.appliedCredits,
      totalTdsApplied: this.appliedTds,
      totalApplied: this.appliedCredits + this.appliedTds,
      remainingBalance: this.remainingBalance,
      billAmount: this.billAmount,
      totalBillAmount: this.totalBillAmount,
      appliedOn: this.form.value.appliedOnDate
    });
  }

  cancel(): void {
    this.ref.close();
  }

  get totalApplied(): number {
    return this.appliedCredits + this.appliedTds;
  }

  get hasCreditsApplied(): boolean {
    return this.appliedCredits > 0 || this.appliedTds > 0;
  }
}
